import { Controller } from "@hotwired/stimulus"

// Smoothie Builder configurator. Lives at /smoothies/builder on
// breakfast.co.nz. Server is the source of truth for macros — we POST the
// configuration to /smoothies/preview and render the JSON response.
//
// Buckets mirror Custom::Breakfast::Ingredient::SMOOTHIE_ROLES:
//   base, fruit, vegetable, booster, sweetener, flavour
// Multiple flavours can stack (vanilla + cinnamon, etc.); sweetener is
// single-select. At least one of fruit OR vegetable must be picked plus a
// base — fruit alone is no longer mandatory.
//
// Serving size is a proportional scaler: picking 220ml shrinks every
// ingredient amount by 220/350; picking 500ml scales it up by 500/350.
export default class extends Controller {
  static targets = [
    "base",
    "fruitsContainer", "fruitChip", "fruitHint",
    "vegetablesContainer", "vegetableChip",
    "boostersContainer", "boosterChip",
    "sweetenersContainer", "sweetenerChip",
    "flavoursContainer", "flavourChip",
    "serving",
    "title", "saveButton", "status",
    "macros",
    "macroCalories", "macroProtein", "macroCarbs",
    "macroFat", "macroFibre", "macroSugar"
  ]

  static values = {
    previewUrl: String,
    createUrl: String
  }

  static MAX_FRUITS = 3
  static MAX_VEGETABLES = 3
  static MAX_BOOSTERS = 3
  static MAX_FLAVOURS = 3
  static DEFAULT_SERVING_ML = 350
  static PREVIEW_DEBOUNCE_MS = 300
  static PENDING_CONFIG_KEY = "breakfast.pending_smoothie"

  connect() {
    this._previewTimer = null
    this._restorePendingConfig()
    this._refreshFruitHint()
    this._schedulePreview()
  }

  disconnect() {
    if (this._previewTimer) clearTimeout(this._previewTimer)
  }

  // ---- input change handlers ----

  onChange() {
    this._schedulePreview()
  }

  toggleFruit(event) {
    this._toggleChip(event.currentTarget, this._selectedFruitChips(), this.constructor.MAX_FRUITS)
    this._refreshFruitHint()
    this._schedulePreview()
  }

  toggleVegetable(event) {
    this._toggleChip(event.currentTarget, this._selectedVegetableChips(),
                     this.constructor.MAX_VEGETABLES)
    this._refreshFruitHint()
    this._schedulePreview()
  }

  toggleBooster(event) {
    this._toggleChip(event.currentTarget, this._selectedBoosterChips(),
                     this.constructor.MAX_BOOSTERS)
    this._schedulePreview()
  }

  toggleFlavour(event) {
    this._toggleChip(event.currentTarget, this._selectedFlavourChips(),
                     this.constructor.MAX_FLAVOURS)
    this._schedulePreview()
  }

  // Single-select: clicking the active chip deselects; clicking another
  // switches the selection.
  selectSweetener(event) {
    const chip = event.currentTarget
    const wasSelected = chip.dataset.selected === "true"
    this.sweetenerChipTargets.forEach(c => { c.dataset.selected = "false" })
    if (!wasSelected) chip.dataset.selected = "true"
    this._schedulePreview()
  }

  // ---- save flow ----

  async save(event) {
    event.preventDefault()

    const config = this._collectConfiguration()
    if (!this._validForSave(config)) {
      this._flashStatus("Pick a base and at least one fruit or veg before saving.")
      return
    }

    this._setSaving(true)
    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({
          smoothie: {
            configuration: config,
            title: this.titleTarget.value.trim()
          }
        })
      })
      let data
      try {
        data = await res.json()
      } catch (_jsonErr) {
        this._flashStatus(`Couldn't save (HTTP ${res.status}).`)
        return
      }

      if (data.status === "saved") {
        window.location.assign(data.smoothie_url)
        return
      } else if (data.status === "anon_limit_reached") {
        // Stash the in-progress configuration so we can re-hydrate the
        // builder after the user signs up, confirms email, and logs in.
        this._stashPendingConfig(config)
        this._flashStatus("You've saved 3 already. Sign up to keep saving — we'll keep this one for you.")
        setTimeout(() => window.location.assign(data.signup_url), 1500)
        return
      } else if (data.status === "invalid") {
        this._flashStatus(`Couldn't save: ${(data.errors || []).join(", ")}`)
        return
      }
      this._flashStatus("Couldn't save — please try again.")
    } catch (e) {
      console.warn("Smoothie save failed", e)
      this._flashStatus("Network hiccup — please try again.")
    } finally {
      this._setSaving(false)
    }
  }

  // ---- internals ----

  // ---- pending-config stash & restore (anon → signup → return path) ----

  _stashPendingConfig(config) {
    try {
      window.localStorage.setItem(
        this.constructor.PENDING_CONFIG_KEY, JSON.stringify(config)
      )
    } catch (_e) {
      // localStorage may be unavailable (private browsing, quota); not fatal.
    }
  }

  _restorePendingConfig() {
    let raw
    try {
      raw = window.localStorage.getItem(this.constructor.PENDING_CONFIG_KEY)
    } catch (_e) {
      return
    }
    if (!raw) return

    try {
      const config = JSON.parse(raw)
      this._applyConfiguration(config)
      window.localStorage.removeItem(this.constructor.PENDING_CONFIG_KEY)
      this._flashStatus("Welcome back — we've restored your smoothie.")
    } catch (_e) {
      try { window.localStorage.removeItem(this.constructor.PENDING_CONFIG_KEY) } catch (_) { /* noop */ }
    }
  }

  // Apply a stashed configuration to the form: select base, mark chips,
  // set serving size. The shape mirrors what _collectConfiguration emits.
  _applyConfiguration(config) {
    if (!config || typeof config !== "object") return

    if (config.base?.ingredient_id && this.hasBaseTarget) {
      this.baseTarget.value = String(config.base.ingredient_id)
    }

    if (config.serving_size_ml && this.hasServingTarget) {
      const radio = this.servingTargets.find(
        r => parseInt(r.value, 10) === parseInt(config.serving_size_ml, 10)
      )
      if (radio) radio.checked = true
    }

    this._selectChipsBySlug(this._safeChipTargets("fruitChip"), config.fruits)
    this._selectChipsBySlug(this._safeChipTargets("vegetableChip"), config.vegetables)
    this._selectChipsBySlug(this._safeChipTargets("boosterChip"), config.boosters)
    this._selectChipsBySlug(this._safeChipTargets("flavourChip"), config.flavours)

    if (config.sweetener?.slug) {
      const chip = this._safeChipTargets("sweetenerChip")
        .find(c => c.dataset.slug === config.sweetener.slug)
      if (chip) chip.dataset.selected = "true"
    }
  }

  _selectChipsBySlug(chipTargets, configEntries) {
    if (!chipTargets || !Array.isArray(configEntries)) return
    const slugs = new Set(configEntries.map(e => e?.slug).filter(Boolean))
    chipTargets.forEach(chip => {
      if (slugs.has(chip.dataset.slug)) chip.dataset.selected = "true"
    })
  }

  _safeChipTargets(name) {
    const hasName = `has${name[0].toUpperCase()}${name.slice(1)}Target`
    const targetsName = `${name}Targets`
    return this[hasName] ? this[targetsName] : []
  }

  _toggleChip(chip, currentlySelected, max) {
    const isSelected = chip.dataset.selected === "true"
    if (!isSelected && currentlySelected.length >= max) {
      this._flashStatus(`You can pick up to ${max} for that section.`)
      return
    }
    chip.dataset.selected = isSelected ? "false" : "true"
  }

  _schedulePreview() {
    if (this._previewTimer) clearTimeout(this._previewTimer)
    this._previewTimer = setTimeout(
      () => this._fetchPreview(), this.constructor.PREVIEW_DEBOUNCE_MS
    )
  }

  async _fetchPreview() {
    const config = this._collectConfiguration()
    if (!this._minimallyComplete(config)) return

    try {
      const res = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ configuration: config })
      })
      if (!res.ok) return
      const data = await res.json()
      this._renderMacros(data.macros)
    } catch (e) {
      console.warn("Smoothie preview failed", e)
    }
  }

  _renderMacros(macros) {
    if (!macros) return
    this._setMacroIfPresent("Calories", macros.calories_kcal, "kcal")
    this._setMacroIfPresent("Protein", macros.protein_g, "g")
    this._setMacroIfPresent("Carbs", macros.carbs_g, "g")
    this._setMacroIfPresent("Fat", macros.fat_g, "g")
    this._setMacroIfPresent("Fibre", macros.fibre_g, "g")
    this._setMacroIfPresent("Sugar", macros.sugar_g, "g")
  }

  _setMacroIfPresent(label, value, unit) {
    const targetName = `macro${label}Target`
    if (!this[targetName]) return
    const formatted = (value === undefined || value === null) ? "—" : `${value} ${unit}`
    this[targetName].textContent = formatted
  }

  // Build the configuration JSONB sent to the server. Ingredient amounts
  // are scaled by (serving_size_ml / DEFAULT_SERVING_ML) so picking a
  // small/regular/large glass actually changes the recipe (and macros).
  _collectConfiguration() {
    const baseOpt = this._selectedOption(this.hasBaseTarget ? this.baseTarget : null)
    const sweetenerChip = this._selectedSweetenerChip()
    const servingMl = this._currentServingMl()
    const scale = servingMl / this.constructor.DEFAULT_SERVING_ML

    return {
      base:       baseOpt ? this._optionToEntry(baseOpt, "amount_ml", scale) : null,
      fruits:     this._chipsToEntries(this._selectedFruitChips(), "amount_g", scale, true),
      vegetables: this._chipsToEntries(this._selectedVegetableChips(), "amount_g", scale, false),
      boosters:   this._chipsToEntries(this._selectedBoosterChips(), "amount_g", scale, false),
      sweetener:  sweetenerChip ? this._chipToEntry(sweetenerChip, "amount_g", scale) : null,
      flavours:   this._selectedFlavourChips().map(chip => this._chipToFlavourEntry(chip, scale)),
      serving_size_ml: servingMl,
      servings: 1
    }
  }

  _currentServingMl() {
    if (!this.hasServingTarget) return this.constructor.DEFAULT_SERVING_ML
    const checked = this.servingTargets.find(r => r.checked)
    return checked ? parseInt(checked.value, 10) : this.constructor.DEFAULT_SERVING_ML
  }

  _optionToEntry(opt, amountKey, scale) {
    return {
      ingredient_id: parseInt(opt.value, 10),
      slug: opt.dataset.slug,
      [amountKey]: this._scaleAmount(opt.dataset.defaultAmount, scale)
    }
  }

  _chipsToEntries(chips, amountKey, scale, includePosition) {
    return chips.map((chip, idx) => {
      const entry = this._chipToEntry(chip, amountKey, scale)
      if (includePosition) entry.position = idx + 1
      return entry
    })
  }

  _chipToEntry(chip, amountKey, scale) {
    return {
      ingredient_id: parseInt(chip.dataset.id, 10),
      slug: chip.dataset.slug,
      [amountKey]: this._scaleAmount(chip.dataset.defaultAmount, scale)
    }
  }

  // Flavours can be solid (g) or liquid (ml) — brewed coffee is the only
  // ml flavour today, but the per-chip data-default-unit attribute carries
  // it cleanly so future flavours can opt into either.
  _chipToFlavourEntry(chip, scale) {
    const unit = chip.dataset.defaultUnit === "ml" ? "amount_ml" : "amount_g"
    return this._chipToEntry(chip, unit, scale)
  }

  _scaleAmount(rawAmount, scale) {
    return Math.round(parseInt(rawAmount, 10) * scale)
  }

  _selectedOption(selectEl) {
    if (!selectEl) return null
    const opt = selectEl.options[selectEl.selectedIndex]
    return (opt && opt.value) ? opt : null
  }

  _selectedFruitChips() {
    return this.hasFruitChipTarget ? this.fruitChipTargets.filter(c => c.dataset.selected === "true") : []
  }

  _selectedVegetableChips() {
    return this.hasVegetableChipTarget ? this.vegetableChipTargets.filter(c => c.dataset.selected === "true") : []
  }

  _selectedBoosterChips() {
    return this.hasBoosterChipTarget ? this.boosterChipTargets.filter(c => c.dataset.selected === "true") : []
  }

  _selectedSweetenerChip() {
    if (!this.hasSweetenerChipTarget) return null
    return this.sweetenerChipTargets.find(c => c.dataset.selected === "true") || null
  }

  _selectedFlavourChips() {
    return this.hasFlavourChipTarget ? this.flavourChipTargets.filter(c => c.dataset.selected === "true") : []
  }

  // The minimum required for the macro preview / save: a base AND at
  // least one fruit or vegetable.
  _minimallyComplete(config) {
    return Boolean(config.base) &&
           ((config.fruits && config.fruits.length > 0) ||
            (config.vegetables && config.vegetables.length > 0))
  }

  _validForSave(config) {
    return this._minimallyComplete(config)
  }

  _refreshFruitHint() {
    if (!this.hasFruitHintTarget) return
    const fruitCount = this._selectedFruitChips().length
    const vegCount = this._selectedVegetableChips().length
    if (fruitCount === 0 && vegCount === 0) {
      this.fruitHintTarget.textContent = "Pick at least one fruit or veg below."
    } else if (fruitCount > 0 && vegCount > 0) {
      this.fruitHintTarget.textContent =
        `${fruitCount} of ${this.constructor.MAX_FRUITS} fruits, ${vegCount} of ${this.constructor.MAX_VEGETABLES} veg.`
    } else if (fruitCount > 0) {
      this.fruitHintTarget.textContent =
        `${fruitCount} of ${this.constructor.MAX_FRUITS} fruits selected.`
    } else {
      this.fruitHintTarget.textContent =
        `${vegCount} of ${this.constructor.MAX_VEGETABLES} veg selected — fruit optional.`
    }
  }

  _flashStatus(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    setTimeout(() => {
      if (this.statusTarget.textContent === text) this.statusTarget.textContent = ""
    }, 4000)
  }

  _setSaving(saving) {
    if (!this.hasSaveButtonTarget) return
    this.saveButtonTarget.disabled = saving
    this.saveButtonTarget.textContent = saving ? "Saving..." : "Save my smoothie"
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this._csrfToken()
    }
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
